/**
 * DTOs du domaine fundraising (cagnottes) — Phase 9.
 *
 * Pas de service dédié (plan Phase 9, guide §6.3) : une collecte est portée
 * par les types de transaction `POT_CONTRIBUTION` / `POT_WITHDRAWAL` du
 * wallet-service (annexe A) — il n'existe pas d'entité « collecte » dans la
 * collection Postman. La « création » d'une collecte est donc la première
 * contribution vers le portefeuille de la cagnotte, identifiée par
 * `customerReference`. Les interfaces ci-dessous répliquent le sous-ensemble
 * utile des DTOs canoniques (`@/features/wallet/api/dto`,
 * `@/features/fee-engine/api/dto`) — règle d'architecture : jamais d'import
 * croisé entre features, seuls les enums de `@/features/shared` et les
 * endpoints ré-exportés par `@/core/api/endpoints` sont partagés.
 */
import type {
  CustomerSegment,
  DestinationExternalType,
  FeePayerFeeEngine,
  TransactionStatus,
} from "@/features/shared/api/enums";

/** Types de transaction wallet-service d'une cagnotte (annexe A). */
export const POT_TRANSACTION_TYPES = ["POT_CONTRIBUTION", "POT_WITHDRAWAL"] as const;
export type PotTransactionType = (typeof POT_TRANSACTION_TYPES)[number];

/** Corps d'une contribution — `POST /Transactions/initiate` (§5.3). */
export interface ContributionInitiateRequestDto {
  type: "POT_CONTRIBUTION";
  productCode: string;
  sourceWalletId: string;
  /** Portefeuille de la collecte (cagnotte). */
  destinationWalletId: string;
  destinationExternalType: DestinationExternalType;
  /** Montant de la contribution, entier en unité mineure (§4.3). */
  amountMinor: number;
  currency: string;
  customerSegment: CustomerSegment;
  /** Party contributeur (réf. logique kyc-service.party.id). */
  partyId?: string | null;
  /** Référence de la collecte (identifie la cagnotte côté métier). */
  customerReference?: string | null;
}

/** Transaction créée — sous-ensemble du TransactionDto du guide §6.3. */
export interface ContributionTransactionDto {
  id: string;
  reference: string;
  destinationWalletId?: string | null;
  amountMinor: number;
  feeAmountMinor: number;
  currency: string;
  status: TransactionStatus;
}

/* --------------------------------- Devis de frais -------------------------------- */

/**
 * Corps d'un devis — `POST /fee-quotes` (§6.5). Aligné sur le body Postman
 * `FeeQuoteRequestDto` : `transactionReference` est **requis** par le
 * fee-engine (désérialisation JSON). Les identifiants optionnels partent
 * en `null` explicite (`providerId`, `beneficiaryPartyId`, `promoCode`,
 * `partyId`).
 */
export interface ContributionFeeQuoteRequestDto {
  productCode: string;
  providerId?: string | null;
  customerSegment: CustomerSegment;
  feePurpose: "CUSTOMER_FEE";
  /** Montant de la contribution, entier en unité mineure (§4.3). */
  amountMinor: number;
  currency: string;
  /**
   * Référence de corrélation du devis (journalisation fee-engine).
   * La contribution n'existe pas encore à la preview : UUID client.
   * Distinct de `customerReference` (identifiant de cagnotte).
   */
  transactionReference: string;
  beneficiaryPartyId?: string | null;
  promoCode?: string | null;
  partyId?: string | null;
}

/** Réponse d'un devis — sous-ensemble du DTO canonique (§6.5). */
export interface ContributionFeeQuoteResponseDto {
  grossFeeAmountMinor: number;
  discountAmountMinor: number;
  netFeeAmountMinor: number;
  payer: FeePayerFeeEngine;
  currency: string;
  /** Aucun barème actif pour le triplet demandé — facturation à 0. */
  noActiveRule: boolean;
}
